import {
  Copy,
  FilePlus2,
  FileText,
  MoreHorizontal,
  Pencil,
  Sparkles,
  Star,
  Trash2,
} from "lucide-react";
import type { JSX } from "react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LoadErrorBanner } from "@/components/ui/redesign/page";
import { V3Button, V3IconButton } from "@/components/ui/v3/button";
import { V3ConfirmSheet } from "@/components/ui/v3/confirm-sheet";
import {
  V3_MENU_CONTENT,
  V3_MENU_ITEM,
  V3_MENU_ITEM_DESTRUCTIVE,
} from "@/components/ui/v3/menu";
import { toast } from "@/hooks/use-toast";
import {
  createReferralTemplate,
  deleteReferralTemplate,
  type ReferralBuiltIn,
  type ReferralExtraction,
  type ReferralTemplate,
  updateReferralTemplate,
  useReferralTemplates,
} from "@/lib/referral-templates/queries";
import {
  type ReferralSpecialtyKey,
  specialtyOptions,
} from "@/lib/referral-templates/specialties";
import { cn } from "@/lib/utils";
import { ReferralFromExampleDialog } from "./ReferralFromExampleDialog";
import { ReferralTag } from "./ReferralLetterDialog";
import {
  ExtractionNotice,
  type ReferralEditorValue,
  ReferralTemplateEditor,
} from "./ReferralTemplateEditor";

type Editing = {
  key: number;
  title: string;
  initial: ReferralEditorValue;
  publicId?: string;
  notice?: { identifiersRemoved: boolean; summary: string };
};

const BLANK: ReferralEditorValue = {
  name: "",
  specialty: null,
  sections: [],
  instructions: "",
  layout: "",
  isDefault: false,
  source: "manual",
};

const ROW =
  "flex min-h-[72px] items-center gap-3 border-b border-v3-border px-4 py-3 last:border-b-0 sm:px-5";
const CARD =
  "overflow-hidden bg-v3-panel shadow-v3-hairline max-sm:-mx-4 sm:rounded-v3-panel";
const GROUP =
  "mb-2 px-1 text-[12.5px] font-semibold uppercase tracking-wide text-v3-text-3";
const ICON =
  "flex size-10 shrink-0 items-center justify-center rounded-[10px] bg-v3-surface-2 text-v3-text-2 shadow-v3-hairline [&_svg]:size-[18px]";

// Templates → "Referral letters" (VITE_REFERRAL_TEMPLATES_ENABLED): the
// built-in letters and the clinician's own, made by hand or from one of their
// letters. Self-contained: it reads and writes through its own queries.
export const ReferralTemplatesSection = (): JSX.Element => {
  const { t } = useTranslation();
  const { items, builtIns, loading, error, ready, refresh } =
    useReferralTemplates();
  const [exampleOpen, setExampleOpen] = useState(false);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<ReferralTemplate | null>(null);
  const options = useMemo(
    () =>
      specialtyOptions((key: ReferralSpecialtyKey) =>
        t(`referral.specialties.${key}`),
      ),
    [t],
  );
  const specialtyLabel = (name: string | null): string | null =>
    name
      ? (options.find((option) => option.name === name)?.label ?? name)
      : null;

  const open = (value: Omit<Editing, "key">): void => {
    setSaveError(null);
    setEditing({ ...value, key: Date.now() });
  };
  const edit = (template: ReferralTemplate): void => {
    open({
      title: t("referral.editor.editTitle"),
      publicId: template.public_id,
      initial: {
        name: template.name,
        specialty: template.specialty,
        sections: template.sections,
        instructions: template.instructions,
        layout: template.layout,
        isDefault: template.isDefault,
      },
    });
  };
  const fromBuiltIn = (builtIn: ReferralBuiltIn): void => {
    open({
      title: t("referral.editor.copyTitle"),
      initial: {
        name: t("referral.templates.copyName", {
          name: t(`referral.builtIn.${builtIn.key}.name`),
        }),
        specialty: null,
        sections: builtIn.sections,
        instructions: builtIn.instructions,
        layout: builtIn.layout,
        isDefault: false,
        source: "built-in",
      },
    });
  };
  const fromExample = (extraction: ReferralExtraction): void => {
    setExampleOpen(false);
    open({
      title: t("referral.editor.exampleTitle"),
      initial: { ...extraction.draft, isDefault: false, source: "example" },
      notice: {
        identifiersRemoved: extraction.identifiersRemoved,
        summary: extraction.summary,
      },
    });
  };
  const save = async (value: ReferralEditorValue): Promise<void> => {
    if (!editing) return;
    setSaving(true);
    setSaveError(null);
    try {
      if (editing.publicId)
        await updateReferralTemplate(editing.publicId, value);
      else await createReferralTemplate(value);
      setEditing(null);
      toast({ variant: "success", title: t("referral.templates.saved") });
    } catch {
      setSaveError(t("referral.templates.saveFailed"));
    } finally {
      setSaving(false);
    }
  };
  const setDefault = async (template: ReferralTemplate): Promise<void> => {
    try {
      await updateReferralTemplate(template.public_id, {
        isDefault: !template.isDefault,
      });
    } catch {
      toast({ variant: "error", title: t("referral.templates.saveFailed") });
    }
  };
  const remove = async (template: ReferralTemplate): Promise<void> => {
    try {
      await deleteReferralTemplate(template.public_id);
      toast({ variant: "success", title: t("referral.templates.deleted") });
    } catch {
      toast({ variant: "error", title: t("referral.templates.deleteFailed") });
    }
  };

  return (
    <div className="space-y-6 font-v3" data-testid="referral-templates">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0 space-y-1">
          <h2 className="font-v3-display text-[19px] font-semibold tracking-[-0.01em] text-v3-text">
            {t("referral.templates.title")}
          </h2>
          <p className="max-w-[56ch] text-[13.5px] leading-5 text-v3-text-2">
            {t("referral.templates.description")}
          </p>
        </div>
        <div className="flex gap-2 max-sm:flex-col">
          <V3Button
            data-testid="referral-from-example-open"
            variant="primary"
            onClick={() => {
              setExampleOpen(true);
            }}
          >
            <Sparkles aria-hidden />
            {t("referral.templates.fromExample")}
          </V3Button>
          <V3Button
            data-testid="referral-new-blank"
            variant="secondary"
            onClick={() => {
              open({ title: t("referral.editor.newTitle"), initial: BLANK });
            }}
          >
            <FilePlus2 aria-hidden />
            {t("referral.templates.blank")}
          </V3Button>
        </div>
      </div>

      {error && (
        <LoadErrorBanner
          description={t("referral.templates.loadError")}
          title={t("error.unknown")}
          onRetry={() => {
            void refresh();
          }}
        />
      )}

      <section aria-labelledby="referral-own">
        <h3 className={GROUP} id="referral-own">
          {t("referral.templates.yours")}
        </h3>
        <div className={CARD}>
          {loading && (
            <div aria-busy className={cn(ROW, "animate-pulse")} role="status">
              <span className="h-4 w-1/2 rounded bg-v3-surface-2" />
            </div>
          )}
          {ready && items.length === 0 && (
            <div className="px-5 py-6 text-center" data-testid="referral-empty">
              <p className="text-[14px] font-semibold text-v3-text">
                {t("referral.templates.emptyTitle")}
              </p>
              <p className="mx-auto mt-1 max-w-[46ch] text-[13px] leading-5 text-v3-text-2">
                {t("referral.templates.emptyDescription")}
              </p>
            </div>
          )}
          {items.map((template) => (
            <div key={template.public_id} className={ROW}>
              <span aria-hidden className={ICON}>
                <FileText />
              </span>
              <button
                className="min-w-0 flex-1 rounded-lg text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring"
                type="button"
                onClick={() => {
                  edit(template);
                }}
              >
                <span className="flex flex-wrap items-center gap-1.5">
                  <span className="truncate text-[15px] font-semibold text-v3-text">
                    {template.name}
                  </span>
                  {template.isDefault && (
                    <ReferralTag>
                      <Star aria-hidden className="size-3 fill-current" />
                      {t("referral.templates.default")}
                    </ReferralTag>
                  )}
                  {template.source === "example" && (
                    <ReferralTag>
                      {t("referral.templates.fromExampleTag")}
                    </ReferralTag>
                  )}
                </span>
                <span className="mt-0.5 block truncate text-[13px] text-v3-text-2">
                  {[
                    specialtyLabel(template.specialty),
                    template.sections.length
                      ? t("referral.dialog.sectionCount", {
                          count: template.sections.length,
                        })
                      : t("referral.dialog.paragraphs"),
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <V3IconButton
                    label={t("referral.templates.actions", {
                      name: template.name,
                    })}
                  >
                    <MoreHorizontal />
                  </V3IconButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className={V3_MENU_CONTENT}>
                  <DropdownMenuItem
                    className={V3_MENU_ITEM}
                    onSelect={() => {
                      edit(template);
                    }}
                  >
                    <Pencil aria-hidden />
                    {t("referral.templates.edit")}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className={V3_MENU_ITEM}
                    onSelect={() => {
                      void setDefault(template);
                    }}
                  >
                    <Star aria-hidden />
                    {template.isDefault
                      ? t("referral.templates.unsetDefault")
                      : t("referral.templates.setDefault")}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className={cn(V3_MENU_ITEM, V3_MENU_ITEM_DESTRUCTIVE)}
                    onSelect={() => {
                      setDeleting(template);
                    }}
                  >
                    <Trash2 aria-hidden />
                    {t("referral.templates.delete")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ))}
        </div>
      </section>

      <section aria-labelledby="referral-built-in">
        <h3 className={GROUP} id="referral-built-in">
          {t("referral.templates.builtInGroup")}
        </h3>
        <div className={CARD}>
          {builtIns.map((builtIn) => (
            <div key={builtIn.key} className={ROW}>
              <span aria-hidden className={ICON}>
                <FileText />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[15px] font-semibold text-v3-text">
                  {t(`referral.builtIn.${builtIn.key}.name`)}
                </p>
                <p className="mt-0.5 line-clamp-2 text-[13px] leading-[18px] text-v3-text-2">
                  {t(`referral.builtIn.${builtIn.key}.description`)}
                </p>
              </div>
              <V3Button
                variant="secondary"
                onClick={() => {
                  fromBuiltIn(builtIn);
                }}
              >
                <Copy aria-hidden />
                <span className="max-sm:sr-only">
                  {t("referral.templates.customize")}
                </span>
              </V3Button>
            </div>
          ))}
        </div>
      </section>

      <V3ConfirmSheet
        confirmLabel={t("referral.templates.delete")}
        description={
          deleting
            ? t("referral.templates.deleteConfirm", { name: deleting.name })
            : undefined
        }
        destructive
        open={deleting !== null}
        testId="referral-delete"
        title={t("referral.templates.deleteTitle")}
        onConfirm={() => {
          if (deleting) void remove(deleting);
          setDeleting(null);
        }}
        onOpenChange={(next) => {
          if (!next) setDeleting(null);
        }}
      />
      <ReferralFromExampleDialog
        open={exampleOpen}
        onExtracted={fromExample}
        onOpenChange={setExampleOpen}
      />
      {editing && (
        <ReferralTemplateEditor
          key={editing.key}
          error={saveError}
          initial={editing.initial}
          notice={
            editing.notice ? (
              <ExtractionNotice {...editing.notice} />
            ) : undefined
          }
          open
          saving={saving}
          title={editing.title}
          onOpenChange={(next) => {
            if (!next) setEditing(null);
          }}
          onSave={(value) => {
            void save(value);
          }}
        />
      )}
    </div>
  );
};
